import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Fuel, Gauge, MapPin, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { MarkingNotification } from '@/hooks/use-marking-notifications';
import { cn } from '@/lib/utils';

interface MarkingNotificationsFeedProps {
  notifications: MarkingNotification[];
  unreadIds: string[];
  onMarkAsRead: (id: string) => void;
}

export function MarkingNotificationsFeed({
  notifications,
  unreadIds,
  onMarkAsRead,
}: MarkingNotificationsFeedProps) {
  if (notifications.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
        <Gauge className="h-12 w-12 mb-2 opacity-50" />
        <p>Nenhuma marcação registrada</p>
      </div>
    );
  }

  return (
    <ScrollArea className="h-[600px]">
      <div className="space-y-2">
        {notifications.map((notification) => {
          const isUnread = unreadIds.includes(notification.id);
          const isFuel = notification.marking_type === 'fuel';

          return (
            <div
              key={notification.id}
              className={cn(
                "p-4 rounded-lg border transition-colors",
                isUnread ? "bg-accent/50 border-primary/30" : "bg-card"
              )}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 flex-1">
                  <div className={cn(
                    "p-2 rounded-lg",
                    isFuel ? "bg-primary/10 text-primary" : "bg-blue-500/10 text-blue-500"
                  )}>
                    {isFuel ? (
                      <Fuel className="h-5 w-5" />
                    ) : (
                      <Gauge className="h-5 w-5" />
                    )}
                  </div>

                  <div className="flex-1 space-y-1">
                    <div className="flex items-center gap-2">
                      <h4 className="font-semibold text-sm">
                        {notification.fleet_drivers?.name || 'Motorista'}
                      </h4>
                      {isUnread && (
                        <Badge variant="default" className="text-xs">
                          Nova
                        </Badge>
                      )}
                    </div>

                    <p className="text-sm text-muted-foreground">
                      {isFuel ? '⛽ Abastecimento' : '📊 Marcação de KM'} -{' '}
                      {notification.fleet_vehicles?.plate || 'Sem veículo'}
                    </p>

                    {notification.metadata?.location && (
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <MapPin className="h-3 w-3" />
                        <span>
                          {notification.metadata.location.latitude?.toFixed(6)},{' '}
                          {notification.metadata.location.longitude?.toFixed(6)}
                        </span>
                      </div>
                    )}

                    <p className="text-xs text-muted-foreground">
                      {formatDistanceToNow(new Date(notification.created_at), {
                        addSuffix: true,
                        locale: ptBR,
                      })}
                    </p>
                  </div>
                </div>

                {isUnread && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => onMarkAsRead(notification.id)}
                  >
                    <Check className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </ScrollArea>
  );
}
